import { generateUUID } from './util';
import { InferenceFile, Model, Inference, InboxFile, TestResult } from '../types';
import { DatasetFileVersion, DatasetVersion } from '../types/dataset';
import { Label, TransformedLabel } from '../types/label';

export const getResultLabels = (inferenceFile: Inference | InferenceFile, model: Model, allLabels: boolean = false) => {
  if (model?.info?.annotationTypes && inferenceFile?.inferenceResult?.result) {
    let labels: Label[];

    if (model.info.annotationTypes.length) {
      labels = model.info.annotationTypes.reduce((labels, modelAnnotation) => {
        const inferenceResults = inferenceFile.inferenceResult.result.fields.filter(
          inferenceResult => inferenceResult.name === modelAnnotation
        );
        if (inferenceResults.length === 0) {
          labels.push({
            id: generateUUID(),
            name: modelAnnotation,
            source: null,
            page: null,
            word: null,
            location: null,
            reliable: true,
            exists: false,
            manuallyApproved: false,
          });
        } else {
          inferenceResults.forEach(({ positions, ...inferenceResult }) => {
          if(allLabels){
              positions.forEach((pos) => {
              labels.push({
                  word: null,
                  source: null,
                  ...inferenceResult,
                  ...pos,
                  id: generateUUID(),
                });
              })
          } else{
              labels.push({
                      word: null,
                      source: null,
                      ...inferenceResult,
                      ...positions[0],
                      id: generateUUID(),
                    });
          }
          });
        }

        return labels;
      }, [] as Label[]);
    } else {
      labels = inferenceFile.inferenceResult.result.fields?.map(({ positions, ...field }) => {
        return {
          word: null,
          source: null,
          ...field,
          ...positions[0],
          id: generateUUID(),
        };
      });
    }

    if (inferenceFile.inferenceResult.result.tables?.length) {
      labels = labels.concat(...inferenceFile.inferenceResult.result.tables?.map(table => table.labels));
    }

    return labels;
  }

  return [];
};

export const getAnnotationLabels = (inferenceFile: Inference | InferenceFile, model: Model) => {
  if (model && model.info && model.info.annotationTypes && inferenceFile && inferenceFile.annotations) {
    const labels: Label[] = model.info.annotationTypes.reduce((labels, modelAnnotation) => {
      const inferenceAnnotations = inferenceFile.annotations.fields.filter(
        inferenceAnnotation => inferenceAnnotation.name === modelAnnotation
      );
      if (inferenceAnnotations.length === 0) {
        labels.push({
          id: generateUUID(),
          name: modelAnnotation,
          source: null,
          page: null,
          word: null,
          location: null,
          reliable: true,
          exists: false,
          manuallyApproved: false,
        });
      } else {
        inferenceAnnotations.forEach(({ positions, ...inferenceAnnotation }) => {
          labels.push({
            word: null,
            source: null,
            ...inferenceAnnotation,
            ...positions[0],
            id: generateUUID(),
          });
        });
      }

      return labels;
    }, [] as Label[]);
    return labels;
  }

  return [];
};

export const getDatasetLabels = (datasetFileVersion: DatasetFileVersion, datasetVersion: DatasetVersion) => {
  const labels: Label[] = datasetVersion.settings.annotationTypes.reduce(
    (labels, settingsfield: { name: string; color: string; hidden: boolean }) => {
      const datasetResults = datasetFileVersion.annotations.fields.filter(
        datasetAnnotation => datasetAnnotation.name === settingsfield.name && datasetAnnotation.reliable
      );
      if (datasetResults.length === 0) {
        labels.push({
          id: generateUUID(),
          name: settingsfield.name,
          source: null,
          page: null,
          word: null,
          hidden: settingsfield.hidden,
          location: null,
          reliable: true,
          exists: false,
          manuallyApproved: false,
        });
      } else {
        datasetResults.forEach(({ positions, ...datasetAnnotation }) => {
          datasetAnnotation.hidden = settingsfield.hidden;
          datasetAnnotation.color = settingsfield.color;
          labels.push({
            word: null,
            source: null,
            ...datasetAnnotation,
            ...positions[0],
            id: generateUUID(),
          });
        });
      }
      return labels;
    },
    [] as Label[]
  );
  return labels;
};

export const getInboxOrResultFileLabels = (file: InboxFile | TestResult) => {
  const labels: Label[] = [];

  file.resultFields.forEach(field => {
    if (field.exists) {
      labels.push({
        exists: field.exists,
        name: field.name,
        location: field.positions[0].location,
        page: field.positions[0].page,
        reliable: field.hasCorrection ? field.hasCorrection.correct : field.reliable,
        prob: field.positions[0].prob,
        source: field.positions[0].source,
        word: field.positions[0].word,
        id: field.id,
        manuallyApproved: field.hasCorrection && field.hasCorrection.correct ? true : false,
      });
    } else {
      labels.push({
        exists: false,
        name: field.name,
        location: null,
        page: null,
        reliable: false,
        prob: 0,
        source: 'not found',
        word: '',
        id: field.id,
        manuallyApproved: false,
      });
    }
    if (
      field.hasCorrection &&
      !field.hasCorrection.correct &&
      field.hasCorrection.correction?.exists &&
      field.hasCorrection.correction?.position
    ) {
      labels.push({
        exists: true,
        name: field.hasCorrection.name,
        location: field.hasCorrection.correction.position,
        page: field.hasCorrection.correction.page,
        reliable: field.reliable,
        prob: 1,
        source: 'corrected',
        word: field.hasCorrection.correction.word,
        id: field.id + '1',
        manuallyApproved: true,
      });
    }
  });
  return labels;
};

export const transformLabels = (labels: Label[]) => {
  const strippedLabels: TransformedLabel[] = labels.map(({ source, page, location, word, ...label }) => {
    const positions =
      page && location
        ? [
            {
              source,
              page,
              location,
              word,
            },
          ]
        : [];
    return {
      ...label,
      positions,
    };
  });
  return strippedLabels
}